import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useDrop } from "react-dnd";
import { Box, Switch, TextField } from "@mui/material";
import {
  Add,
  VisibilityOutlined,
  DriveFileRenameOutlineOutlined,
} from "@mui/icons-material";
import { Button, DataDialog } from "@app/components/atoms";
import {
  ConfirmDialog,
  ConfirmDialogProps,
  PreviewInspectionDialog,
} from "@app/pages/Features";
import { Card } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/Card";

import {
  CurrentStatusPostEnum,
  DialogTypes,
  ItemCodeEnum,
  ItemTypes,
  ResultType,
  UploadFileType,
} from "@app/constants";
import { REGEX, handleUploadStore, safeSliceGraphemes } from "@app/helpers";
import { MAX_INSPECTION_FORM_ITEMS } from "@app/helpers/inspection-bulk-template.helper";
import { IconInputError, IconWarningFormEdit } from "@app/components/icons";
import {
  RootState,
  setInspectionsTemplate,
  setIsEditDraft,
  setMachineId,
  setShowAlert,
} from "@app/store";
import { postInspectionFormTemplates, putCustomInspectionForm } from "@app/api";
import { InspectionItem, InspectionsType } from "@app/types";
import { useStoreDispatch } from "@app/hooks";

import React from "react";

type InspectionForm = {
  open: boolean;
  setOpen: any;
  onRequestData: any;
  setShowEditAssigments: any;
  setCustomInspectionFormIdEditAssignments: any;
};

export function InspectionFormDialog({
  open,
  setOpen,
  onRequestData,
  setShowEditAssigments,
  setCustomInspectionFormIdEditAssignments,
}: InspectionForm) {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useStoreDispatch();
  const [openDialogConfirm, setOpenDialogConfirm] = useState(false);
  const [openDialogPreview, setOpenDialogPreview] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>({});
  const [meterItemList, setMeterItemList] = useState<InspectionItem[]>([]);
  const [isRequiredOdometer, setIsRequiredOdometer] = useState<boolean>(false);
  const [isRequiredSMR, setIsRequiredSMR] = useState<boolean>(false);
  const [dbPreview, setDbPreview] = useState<any>(null);
  const [dbInspections, setDbInspections] = useState<InspectionsType>();
  const dbInspectionsFromStore = useSelector(
    (state: RootState) => state?.inspection?.inspectionsTemplate
  );
  const { isEditDraft, machineId, currentInspectionInfo } = useSelector(
    (state: RootState) => state?.inspection
  );
  const [mediaUploadErrorMsg, setMediaUploadErrorMsg] = useState("");
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      inspectionItems: [
        {
          inspectionItemId: "",
          name: "",
          previousName: "",
          description: "",
          customInspectionItemMedias: [],
          isNew: false,
          isOpen: false,
          isForcedRequired: false,
          isImmutable: false,
          isRequired: false,
          itemCode: null,
          resultType: ResultType.OK_OR_ANOMARY,
          options: undefined,
        },
      ],
    },
  });
  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    control,
    watch,
    reset,
    trigger,
    formState: { errors, isValid },
  } = methods;
  const { fields, update, remove, append, move } = useFieldArray({
    control,
    name: "inspectionItems",
  });
  const inspectionItemList = watch("inspectionItems");
  const name = watch("name");

  const [, drop] = useDrop(() => ({ accept: ItemTypes.CARD }));

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      move(dragIndex, hoverIndex);
    },
    [move]
  );

  const [isAllMediaTransferred, setIsAllMediaTransferred] = useState<boolean[]>(
    []
  );

  const [allMediaTransferred, setAllMediaTransferred] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const hasNoItems = inspectionItemList.length + meterItemList.length === 0;
      const hasNoInspectionItems = inspectionItemList.length === 0;

      const isInitialized =
        isAllMediaTransferred.length === inspectionItemList.length;

      const allTransferred =
        hasNoItems ||
        hasNoInspectionItems ||
        (isInitialized &&
          isAllMediaTransferred.every((status) => status === true));

      setAllMediaTransferred(allTransferred);
    }, 300);

    return () => clearTimeout(timeout);
  }, [isAllMediaTransferred, inspectionItemList.length, meterItemList.length]);

  const handleDeleteCard = useCallback(
    (index: number) => {
      remove(index);
      setIsAllMediaTransferred((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    },
    [remove]
  );

  const cardCancelRefs = useRef<React.RefObject<{ cancel: () => void }>[]>([]);

  useEffect(() => {
    cardCancelRefs.current = inspectionItemList.map(() => React.createRef());
  }, [inspectionItemList]);

  const handleSetMediaTransferred = useCallback(
    (index: number, isTransferred: boolean) => {
      setIsAllMediaTransferred((prevStatus) => {
        const newStatus = [...prevStatus];
        newStatus[index] = isTransferred;
        return newStatus;
      });
    },
    []
  );

  const updateCard = useCallback(
    (index: number, object: any) => {
      update(index, object);
    },
    [update]
  );

  const renderCard = useCallback(
    (field: any, index: number) => {
      return (
        <Card
          ref={cardCancelRefs.current[index]}
          index={index}
          key={field.inspectionItemId || index}
          id={field.inspectionItemId}
          card={field}
          moveCard={moveCard}
          updateCard={updateCard}
          setValue={setValue}
          deleteCard={handleDeleteCard}
          mediaUploadErrorMsg={mediaUploadErrorMsg}
          setMediaTransferred={(isTransferred: boolean) =>
            handleSetMediaTransferred(index, isTransferred)
          }
        />
      );
    },
    [
      moveCard,
      updateCard,
      setValue,
      handleDeleteCard,
      mediaUploadErrorMsg,
      handleSetMediaTransferred,
    ]
  );

  const handleCloseDialog = () => {
    cardCancelRefs.current.forEach((ref) => {
      ref.current?.cancel();
    });

    setOpenDialogConfirm(false);
    setOpen(false);
    dispatch(setInspectionsTemplate(null));
    dispatch(setMachineId(null));
    dispatch(setIsEditDraft(false));
    setIsRequiredOdometer(false);
    setIsRequiredSMR(false);
    setMeterItemList([]);
    setIsAllMediaTransferred([]);
    setAllMediaTransferred(false);
    reset();
  };

  const inspectionItemLimitReached =
    inspectionItemList.length >= MAX_INSPECTION_FORM_ITEMS;

  const handleAddInspectionItem = () => {
    if (inspectionItemLimitReached) {
      return;
    }

    const newInspectionItems = inspectionItemList.filter((item) => item.isNew);
    let latestNewInspectionItemId = Math.max(
      ...newInspectionItems.map((item) => +item.inspectionItemId)
    );
    if (latestNewInspectionItemId === -Infinity) {
      latestNewInspectionItemId = 0;
    }
    append({
      inspectionItemId: `${latestNewInspectionItemId + 1}`,
      name: "",
      previousName: "",
      description: "",
      customInspectionItemMedias: [],
      isNew: true,
      isOpen: true,
      isForcedRequired: false,
      isImmutable: false,
      isRequired: false,
      itemCode: null,
      resultType: ResultType.OK_OR_ANOMARY,
      options: undefined,
    });
  };

  const handleUploadMedia = async () => {
    const convertData = { ...(handleConvertData() as any) };
    const files: any = [];

    convertData?.customInspectionItems?.forEach((item: any, index: number) => {
      item.customInspectionItemMedias?.forEach(
        (media: any, mediaIndex: number) => {
          if (!media.filePath) {
            files.push({
              index,
              mediaIndex,
              media,
            });
          }
        }
      );
    });
    if (!!files.length) {
      for await (const item of files) {
        const newMedia = (await handleUploadStore(
          UploadFileType.MACHINE_INSPECTIONS,
          item.media
        )) as any;

        const captureDate = item.media?.lastModified
          ? new Date(item.media.lastModified).toISOString()
          : null;

        item.media = newMedia
          ? {
              fileName: newMedia?.name,
              filePath: newMedia?.filePath,
              mediaUrl: newMedia?.filePath,
              mimeType: newMedia?.type,

              captureMediaAt: captureDate,
            }
          : null;
      }
    }
    if (files?.some((item: any) => item.media === null)) {
      return null;
    }
    files?.forEach((file: any) => {
      convertData.customInspectionItems[file.index].customInspectionItemMedias[
        file.mediaIndex
      ] = file.media;
    });
    return convertData;
  };

  const handleConvertData = () => {
    if (!!dbInspections) {
      const newInspectionItemList = inspectionItemList.map(
        (item: InspectionItem) => {
          const baseItem = {
            inspectionItemId: item?.isNew ? undefined : item?.inspectionItemId,
            name: item?.name,
            description: item?.description,
            customInspectionItemMedias: item?.customInspectionItemMedias?.length
              ? item?.customInspectionItemMedias
              : undefined,
            isForcedRequired: item?.isForcedRequired,
            isImmutable: item?.isImmutable,
            isRequired: item?.isRequired,
            itemCode: item?.itemCode,
            resultType: item?.resultType,
          };

          if (
            item?.resultType === ResultType.FREE_TEXT &&
            item?.options?.length
          ) {
            return {
              ...baseItem,
              options: item.options.map((option, index) => ({
                ...(isEditDraft && option.customInspectionItemsOptionsId
                  ? {
                      customInspectionItemsOptionsId:
                        option.customInspectionItemsOptionsId,
                    }
                  : {}),
                name: option.name,
                position: index + 1,
              })),
            };
          }

          return baseItem;
        },
      );
      return {
        name,
        inspectionFormId: dbInspections?.inspectionFormId,
        inspectionFormType: dbInspections?.type,
        customInspectionItems: [...newInspectionItemList, ...meterItemList],
      };
    }
  };

  const handleSaveDraft = async () => {
    if (!!id && !!machineId) {
      setMediaUploadErrorMsg("");
      const convertData = await handleUploadMedia();
      if (!convertData) {
        setMediaUploadErrorMsg(t("messages.errConmpressImg"));
        return;
      }
      !!isEditDraft
        ? putCustomInspectionForm(
            id,
            machineId,
            currentInspectionInfo?.inspectionFormId,
            {
              currentStatus: CurrentStatusPostEnum.DRAFT,
              ...convertData,
            } as InspectionsType
          ).then(() => handleAfterPublishOrSaveDraftInspections())
        : postInspectionFormTemplates(id, machineId, {
            currentStatus: CurrentStatusPostEnum.DRAFT,
            ...convertData,
          } as InspectionsType).then(() =>
            handleAfterPublishOrSaveDraftInspections()
          );
    }
  };

  const handlePreView = () => {
    setDbPreview(handleConvertData());
    setOpenDialogPreview(true);
  };

  const handlePublishInspections = async () => {
    if (!!id && !!machineId) {
      setMediaUploadErrorMsg("");
      const convertData = await handleUploadMedia();
      if (!convertData) {
        setMediaUploadErrorMsg(t("messages.errConmpressImg"));
        return;
      }
      !!isEditDraft
        ? putCustomInspectionForm(
            id,
            machineId,
            currentInspectionInfo?.inspectionFormId,
            {
              currentStatus: CurrentStatusPostEnum.PUBLISHED,
              ...convertData,
            } as any
          ).then((res) => {
            setCustomInspectionFormIdEditAssignments(
              res?.data?.data?.customInspectionFormId
            );
            setShowEditAssigments(true);
            handleAfterPublishOrSaveDraftInspections(
              res?.data?.meta?.successMessage
            );
          })
        : postInspectionFormTemplates(id, machineId, {
            currentStatus: CurrentStatusPostEnum.PUBLISHED,
            ...convertData,
          } as any).then((res) => {
            setCustomInspectionFormIdEditAssignments(
              res?.data?.data?.customInspectionFormId
            );
            setShowEditAssigments(true);
            handleAfterPublishOrSaveDraftInspections(
              res?.data?.meta?.successMessage
            );
          });
    }
  };

  const handleAfterPublishOrSaveDraftInspections = (msg = "") => {
    !!msg && dispatch(setShowAlert(msg));
    onRequestData();
    handleCloseDialog();
  };

  const handleOpenPublishForm = () => {
    setOpenDialogConfirm(true);
    setConfirmDialog({
      classBtnSubmit: "btn-primary",
      typeDialog: DialogTypes.CONFIRM,
      title: "titleConfirmPublish",
      content: "desConfirmPublish",
      btnSubmitText: "formAction.publish",
      onCancel() {
        setOpenDialogConfirm(false);
      },
      onSubmit() {
        setOpenDialogConfirm(false);
        handlePublishInspections();
      },
    });
  };

  const handleClickConfirm = () => {
    setOpenDialogConfirm(true);
    setConfirmDialog({
      classBtnSubmit: "btn-primary",
      typeDialog: DialogTypes.CONFIRM,
      title: "titleConfirm",
      content: "",
      btnSubmitText: "ok",
      onCancel() {
        setOpenDialogConfirm(false);
      },
      onSubmit() {
        handleCloseDialog();
      },
    });
  };

  const handleOpenSaveDraft = () => {
    setOpenDialogConfirm(true);
    setConfirmDialog({
      classBtnSubmit: "btn-primary",
      typeDialog: DialogTypes.SUCCESS,
      title: "titleSaveDraft",
      content: "desSaveDraft",
      btnSubmitText: "formAction.save",
      onCancel() {
        setOpenDialogConfirm(false);
      },
      onSubmit() {
        setOpenDialogConfirm(false);
        handleSaveDraft();
      },
    });
  };

  const onSubmit = () => {
    handleOpenPublishForm();
  };

  const handleConvertSetData = useCallback(
    (db: InspectionsType) => {
      const isBulkUpload = !!(
        db as InspectionsType & { isBulkUpload?: boolean }
      )?.isBulkUpload;
      const hasBulkUploadItemError = (item: InspectionItem) => {
        const segmenter = new Intl.Segmenter(undefined, {
          granularity: "grapheme",
        });
        const name = item?.name ?? "";
        const description = item?.description ?? "";
        const hasMissingPredefinedOptions =
          item?.resultType === ResultType.FREE_TEXT &&
          !(
            item.options?.length &&
            item.options.every((option) => !!option?.name?.trim())
          );

        return (
          !name.trim() ||
          !REGEX.emojiRegex.test(name) ||
          !REGEX.commonNameFieldRegex.test(name) ||
          [...segmenter.segment(name)].length > 100 ||
          !REGEX.emojiRegex.test(description) ||
          !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(description) ||
          [...segmenter.segment(description)].length > 150 ||
          hasMissingPredefinedOptions
        );
      };
      const currentInspectionItems = db?.inspectionItems
        .filter(
          (item: InspectionItem) =>
            item?.resultType === ResultType.OK_OR_ANOMARY ||
            item?.resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE ||
            item?.resultType === ResultType.NONE ||
            item?.resultType === ResultType.FREE_TEXT,
        )
        .map((item: InspectionItem) => {
          return {
            ...item,
            customInspectionItemMedias: item.inspectionItemMedias,
            options: item.options ?? [],
            isNew: item.isNew ?? false,
            isOpen: isBulkUpload ? hasBulkUploadItemError(item) : false,
          };
        });
      setValue("name", db?.name, {
        shouldDirty: isBulkUpload,
        shouldValidate: isBulkUpload,
      });
      setValue("inspectionItems", currentInspectionItems as any, {
        shouldDirty: isBulkUpload,
        shouldValidate: isBulkUpload,
      });
      setDbInspections(db);
      setMeterItemList(
        db?.inspectionItems?.filter(
          (item: InspectionItem) =>
            item?.resultType === ResultType?.NUMERIC && !!item?.itemCode
        )
      );

      if (isBulkUpload) {
        window.setTimeout(() => {
          setFocus("name");
          trigger(["name", "inspectionItems"]);
        }, 0);
      }
    },
    [setFocus, setValue, trigger]
  );

  const handleSetMeter = (index: number, card?: any) => {
    meterItemList.splice(index, 1, {
      ...meterItemList[index],
      isRequired: !card?.isRequired,
    });
    card?.itemCode === ItemCodeEnum.ODOMETER
      ? setIsRequiredOdometer(!card?.isRequired)
      : setIsRequiredSMR(!card?.isRequired);
    setMeterItemList([...meterItemList]);
  };

  useEffect(() => {
    if (dbInspectionsFromStore?.inspectionItems) {
      handleConvertSetData(dbInspectionsFromStore);
    }
  }, [dbInspectionsFromStore, handleConvertSetData]);

  const [initialDelayPassed, setInitialDelayPassed] = useState(false);

  useEffect(() => {
    if (open) {
      setInitialDelayPassed(false);
      const timer = setTimeout(() => {
        setInitialDelayPassed(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <>
      <DataDialog
        title={t("addInspection")}
        open={open}
        setOpen={setOpen}
        disableBackdropClick={true}
        className="dialog-inspection-form"
        action={
          <div className="flex header-actions">
            <Button
              className="btn-no-border-new"
              disabled={
                !initialDelayPassed ||
                !isValid ||
                inspectionItemList?.length + meterItemList?.length < 2 ||
                !allMediaTransferred
              }
              onClick={() => handleOpenSaveDraft()}
            >
              <DriveFileRenameOutlineOutlined className="icon-border-color" />
              <span className="sp-d-none ml-8">{t("saveDraft")}</span>
            </Button>
            <Button
              className="btn-no-border-new"
              disabled={
                !initialDelayPassed ||
                !isValid ||
                inspectionItemList?.length + meterItemList?.length < 2 ||
                !allMediaTransferred
              }
              onClick={() => handlePreView()}
            >
              <VisibilityOutlined className="icon-visibility" />
              <span className="sp-d-none ml-8">{t("preview")}</span>
            </Button>
          </div>
        }
        dialogActions={
          <div className="flex align-items-center justify-content-end">
            <Button
              className="btn-no-border"
              onClick={() => handleClickConfirm()}
            >
              <span>{t("dialogReport.cancel")}</span>
            </Button>
            <Button
              className="btn-primary ml-15"
              onClick={handleSubmit(onSubmit)}
              disabled={
                !initialDelayPassed ||
                !isValid ||
                inspectionItemList?.length + meterItemList?.length < 2 ||
                !allMediaTransferred
              }
            >
              {t("formAction.publish")}
            </Button>
          </div>
        }
      >
        <FormProvider {...methods}>
          <Box component="form" autoComplete="off">
            {!allMediaTransferred && (
              <div
                className="warning flex justify-content-start"
                style={{ marginTop: 0, marginBottom: "10px" }}
              >
                <IconWarningFormEdit />
                <span
                  style={{ marginLeft: 12, flex: 1 }}
                  className="txt-black-1"
                >
                  {t("formEdit.warningUpdateItem1")}
                </span>
              </div>
            )}

            <div className="wrapper-text-field">
              <TextField
                {...register("name", {
                  validate: {
                    hasEmoji: (value) => REGEX.emojiRegex.test(value),
                    hasInvalidChar: (value) =>
                      REGEX.commonNameFieldRegex.test(value),
                    required: (value) => !!value.trim(),
                    maxGraphemes: (value) => {
                      const segmenter = new Intl.Segmenter(undefined, {
                        granularity: "grapheme",
                      });
                      return [...segmenter.segment(value)].length <= 50;
                    },
                  },
                  onBlur: (e) =>
                    setValue("name", e.target.value?.trim(), {
                      shouldValidate: true,
                    }),
                  onChange: (e) => {
                    const safeValue = safeSliceGraphemes(e.target.value, 50);

                    setValue("name", safeValue, {
                      shouldValidate: true,
                    });
                  },
                })}
                error={!!errors.name?.type}
                id={errors.name?.type ? "outlined-error" : "outlined-required"}
                label={`${t("inspectionTable.inspectionName")}*`}
                className="input-field"
                value={name}
              />
              {errors.name?.type && <IconInputError />}
            </div>
            {errors.name ? (
              <span className="txt-red fs-12 ml-15">
                {errors.name?.type === "required" && t("messages.require")}
                {errors.name?.type === "hasInvalidChar" &&
                  t("messages.validCharacters")}
                {errors.name?.type === "hasEmoji" &&
                  t("messages.validCharacters")}
              </span>
            ) : (
              <span className="fs-12 ml-15 txt-gray-1">
                &#42;{t("required")}
              </span>
            )}

            <ul className="cards mt-24" ref={drop}>
              {fields?.map(renderCard)}
            </ul>

            <div className="flex-centered mt-24">
              <Button
                className="btn-secondary"
                disabled={inspectionItemLimitReached}
                onClick={handleAddInspectionItem}
              >
                <Add className="icon-add" />
                {t("addInspectionItem")}
              </Button>
            </div>

            <ul className="mt-24 cards bg-gray">
              {meterItemList?.map((card: InspectionItem, index: number) => (
                <li className="card-item" key={index}>
                  <div
                    className={`card-item-title flex-centered  justify-content-between ${
                      card?.itemCode === ItemCodeEnum.ODOMETER && "have-switch"
                    }`}
                  >
                    <span
                      className={`txt-card ${
                        !!card?.isForcedRequired ? "txt-gray-2" : ""
                      }`}
                    >
                      {card?.name}
                      {card?.itemCode === ItemCodeEnum.ODOMETER
                        ? (!!isRequiredOdometer || !!card?.isRequired) && "*"
                        : card?.itemCode === ItemCodeEnum.SERVICE_METER
                        ? (!!isRequiredSMR || !!card?.isRequired) && "*"
                        : !!card?.isRequired && "*"}
                    </span>
                    {!card?.isForcedRequired && (
                      <div className="d-flex align-items-center">
                        <span className="fs-14 txt-gray-3 mr-15">
                          {t("required")}
                        </span>
                        {card?.itemCode === ItemCodeEnum.ODOMETER ? (
                          <Switch
                            className={`btn-switch ${
                              (!!isRequiredOdometer || !!card?.isRequired) &&
                              "checked"
                            }
                          `}
                            checked={card?.isRequired || isRequiredOdometer}
                            onChange={() => {
                              handleSetMeter(index, card);
                            }}
                          />
                        ) : (
                          <Switch
                            className={`btn-switch ${
                              (!!isRequiredSMR || !!card?.isRequired) &&
                              "checked"
                            }
                          `}
                            checked={card?.isRequired || isRequiredSMR}
                            onChange={() => {
                              handleSetMeter(index, card);
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Box>
        </FormProvider>
      </DataDialog>

      <ConfirmDialog
        open={openDialogConfirm}
        setOpen={setOpenDialogConfirm}
        {...confirmDialog}
      />

      <PreviewInspectionDialog
        open={openDialogPreview}
        setOpen={setOpenDialogPreview}
        data={dbPreview}
      />
    </>
  );
}
